import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportIcon from '@mui/icons-material/Report';

import ChannelSurveyMapView from './ChannelSurveyMapView';
import { api } from '../../services/api';
import {
  DEFAULT_ACS_RSSI_FLOOR_DBM,
  SURVEY_MAP_MARKER_RADIUS_M,
  SURVEY_MARKER_SOURCE,
  buildChannelSurveyMarkers,
  buildEvilTwinMarkers,
  collectSurveyChannelsFromDevices,
  describeEmptyChannelSurvey,
  EVIL_TWIN_SURVEY_MAX_AGE_HOURS,
  summarizeChannelSurvey,
} from '../../utils/acsSurveyMapUtils';
import { commitNumericDraft } from '../../utils/numericFieldUtils';

const parseSurveyDevicesResponse = (response) => (
  Array.isArray(response?.devices) ? response.devices : (Array.isArray(response) ? response : [])
);

const ChannelSurveyTab = ({ isActive }) => {
  const [surveyDevices, setSurveyDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);

  const [minRssiDbm, setMinRssiDbm] = useState(DEFAULT_ACS_RSSI_FLOOR_DBM);
  const [minRssiDraft, setMinRssiDraft] = useState(String(DEFAULT_ACS_RSSI_FLOOR_DBM));
  const [bandFilter, setBandFilter] = useState('all');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [markerSourceFilter, setMarkerSourceFilter] = useState(SURVEY_MARKER_SOURCE.ALL);
  const [showEvilTwin, setShowEvilTwin] = useState(false);
  const [evilTwinResult, setEvilTwinResult] = useState(null);
  const [evilTwinLoading, setEvilTwinLoading] = useState(false);
  const [evilTwinError, setEvilTwinError] = useState(null);

  const loadSurveyDevices = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.getDevices({ compatible: 'jnet2', includeAcsSurvey: true });
      setSurveyDevices(parseSurveyDevicesResponse(response));
    } catch (err) {
      setLoadError(err?.message || 'Failed to load ACS survey data');
      setSurveyDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvilTwinAnalysis = useCallback(async () => {
    setEvilTwinLoading(true);
    setEvilTwinError(null);
    try {
      const result = await api.getACSEvilTwinAnalysis({
        minRssiDbm,
        band: bandFilter,
        maxAgeH: EVIL_TWIN_SURVEY_MAX_AGE_HOURS,
      });
      setEvilTwinResult(result);
    } catch (err) {
      setEvilTwinResult(null);
      setEvilTwinError(err?.message || 'Failed to scan for evil-twin SSIDs');
    } finally {
      setEvilTwinLoading(false);
    }
  }, [minRssiDbm, bandFilter]);

  useEffect(() => {
    if (!isActive) return undefined;
    loadSurveyDevices();
    return undefined;
  }, [isActive, loadSurveyDevices]);

  useEffect(() => {
    if (!isActive || !showEvilTwin) return undefined;
    loadEvilTwinAnalysis();
    return undefined;
  }, [isActive, showEvilTwin, loadEvilTwinAnalysis]);

  const channelOptions = useMemo(() => {
    const byBand = collectSurveyChannelsFromDevices(surveyDevices, bandFilter);
    if (bandFilter === '5GHz') return byBand['5GHz'];
    if (bandFilter === '6GHz') return byBand['6GHz'];
    return [...new Set([...byBand['5GHz'], ...byBand['6GHz']])].sort((a, b) => a - b);
  }, [surveyDevices, bandFilter]);

  const surveySummary = useMemo(
    () => summarizeChannelSurvey(surveyDevices),
    [surveyDevices],
  );

  const markerResult = useMemo(
    () => buildChannelSurveyMarkers(surveyDevices, {
      minRssiDbm,
      bandFilter,
      selectedChannels: selectedChannels.length > 0 ? selectedChannels : null,
      markerSourceFilter,
    }),
    [surveyDevices, minRssiDbm, bandFilter, selectedChannels, markerSourceFilter],
  );

  const evilTwinPlot = useMemo(() => {
    if (!showEvilTwin || !evilTwinResult) return { markers: [], unplottable: [] };
    return buildEvilTwinMarkers(evilTwinResult, surveyDevices);
  }, [showEvilTwin, evilTwinResult, surveyDevices]);

  const evilTwinMarkers = evilTwinPlot.markers;
  const unplottableEvilTwins = evilTwinPlot.unplottable;

  const mapMarkers = useMemo(() => {
    if (!showEvilTwin) return markerResult.markers;
    return evilTwinMarkers;
  }, [showEvilTwin, evilTwinMarkers, markerResult.markers]);

  const emptyMessage = useMemo(() => {
    if (showEvilTwin && evilTwinLoading) return null;
    if (showEvilTwin && evilTwinError) return evilTwinError;
    if (showEvilTwin && (evilTwinResult?.alert_count || 0) === 0) {
      return 'No corroborated SSID impersonation matches for the current filters (live SSID, 2+ hearers, 24 h surveys).';
    }
    if (showEvilTwin && evilTwinMarkers.length === 0 && unplottableEvilTwins.length > 0) {
      return 'Matches found but hearer devices have no GPS to plot them on the map.';
    }
    return describeEmptyChannelSurvey({
      loading,
      error: loadError,
      devicesWithSurvey: markerResult.devicesWithSurvey,
      markerCountBeforeGps: markerResult.markerCountBeforeGps,
      mapPlottableCount: markerResult.mapPlottableCount,
      hasChannelFilter: bandFilter !== 'all' || selectedChannels.length > 0,
      markerSourceFilter,
    });
  }, [
    showEvilTwin,
    evilTwinLoading,
    evilTwinError,
    evilTwinResult,
    evilTwinMarkers.length,
    unplottableEvilTwins.length,
    loading,
    loadError,
    markerResult,
    bandFilter,
    selectedChannels.length,
    markerSourceFilter,
  ]);

  const mapEmptyMessage = useMemo(() => {
    if (showEvilTwin && evilTwinLoading) {
      return 'Scanning for corroborated SSID impersonation matches…';
    }
    return emptyMessage;
  }, [showEvilTwin, evilTwinLoading, emptyMessage]);

  const handleRefresh = useCallback(async () => {
    await loadSurveyDevices();
    if (showEvilTwin) {
      await loadEvilTwinAnalysis();
    }
  }, [loadSurveyDevices, showEvilTwin, loadEvilTwinAnalysis]);

  const handleRssiCommit = () => {
    const committed = commitNumericDraft(minRssiDraft, {
      min: -120,
      max: 0,
      fallback: DEFAULT_ACS_RSSI_FLOOR_DBM,
      parse: (v) => parseInt(v, 10),
    });
    setMinRssiDbm(committed);
    setMinRssiDraft(String(committed));
  };

  const handleEvilTwinToggle = () => {
    setSelectedMarkerId(null);
    setShowEvilTwin((prev) => !prev);
  };

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {showEvilTwin
            ? 'Evil twin mode shows only corroborated SSID impersonation alerts (not the full channel survey). Red markers are placed near hearing devices, not at the foreign AP location.'
            : 'ACS channel survey from each jnet2 device, plotted at its GPS. Solid markers are our devices (busiest scanned channel per radio, or selected channels). Dashed purple markers are external APs heard in the ACS neighbour list (non-Juganu / unknown BSSID), placed near the hearing device. Min signal filters which neighbour BSSIDs qualify.'}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${surveySummary.withSurvey} with survey`} />
          {!showEvilTwin ? (
            <>
              <Chip size="small" color="success" variant="outlined" label={`${surveySummary.fresh} fresh`} />
              {surveySummary.stale > 0 ? (
                <Chip size="small" color="warning" variant="outlined" label={`${surveySummary.stale} stale`} />
              ) : null}
              <Chip size="small" variant="outlined" label={`${markerResult.mapPlottableCount} on map`} />
              {markerResult.ownDeviceMarkerCount > 0 ? (
                <Chip size="small" variant="outlined" label={`${markerResult.ownDeviceMarkerCount} Juganu`} />
              ) : null}
              {markerResult.foreignApCount > 0 ? (
                <Chip size="small" color="secondary" variant="outlined" label={`${markerResult.foreignApCount} external APs`} />
              ) : null}
            </>
          ) : (
            <>
              <Chip
                size="small"
                color={evilTwinResult?.alert_count > 0 ? 'error' : 'default'}
                variant="outlined"
                label={`${evilTwinResult?.alert_count ?? 0} alert${(evilTwinResult?.alert_count ?? 0) === 1 ? '' : 's'}`}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`${evilTwinMarkers.length} on map`}
              />
            </>
          )}
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Tooltip title="External APs heard at or above this level">
            <TextField
              size="small"
              label="Min signal"
              value={minRssiDraft}
              onChange={(e) => setMinRssiDraft(e.target.value)}
              onBlur={handleRssiCommit}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRssiCommit(); }}
              sx={{ width: 118 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">dBm</InputAdornment>,
              }}
            />
          </Tooltip>

          <FormControl size="small" sx={{ minWidth: 108 }}>
            <InputLabel id="acs-band-filter-label">Band</InputLabel>
            <Select
              labelId="acs-band-filter-label"
              label="Band"
              value={bandFilter}
              onChange={(e) => {
                setBandFilter(e.target.value);
                setSelectedChannels([]);
              }}
            >
              <MenuItem value="all">5 + 6 GHz</MenuItem>
              <MenuItem value="6GHz">6 GHz</MenuItem>
              <MenuItem value="5GHz">5 GHz</MenuItem>
            </Select>
          </FormControl>

          {!showEvilTwin ? (
            <>
              <FormControl size="small" sx={{ minWidth: 96 }}>
                <InputLabel id="acs-marker-source-label">Show</InputLabel>
                <Select
                  labelId="acs-marker-source-label"
                  label="Show"
                  value={markerSourceFilter}
                  onChange={(e) => setMarkerSourceFilter(e.target.value)}
                >
                  <MenuItem value={SURVEY_MARKER_SOURCE.ALL}>All</MenuItem>
                  <MenuItem value={SURVEY_MARKER_SOURCE.JUGANU}>Juganu</MenuItem>
                  <MenuItem value={SURVEY_MARKER_SOURCE.EXTERNAL}>External</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={channelOptions}
                value={selectedChannels}
                onChange={(_e, value) => setSelectedChannels(value)}
                sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 360 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Channels"
                    placeholder={selectedChannels.length === 0 ? 'Busiest per radio' : ''}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                      sx={{ height: 22 }}
                    />
                  ))}
              />
            </>
          ) : null}

          <Tooltip title={`Show corroborated SSID impersonation only (live SSID, 2+ hearers, ${EVIL_TWIN_SURVEY_MAX_AGE_HOURS} h surveys). Pins show hearer proximity, not AP location.`}>
            <span>
              <Button
                size="small"
                variant={showEvilTwin ? 'contained' : 'outlined'}
                color={showEvilTwin ? 'error' : 'inherit'}
                disabled={evilTwinLoading}
                startIcon={evilTwinLoading ? <CircularProgress size={14} color="inherit" /> : <ReportIcon fontSize="small" />}
                onClick={handleEvilTwinToggle}
                sx={{ minWidth: 0, px: 1.25, whiteSpace: 'nowrap' }}
              >
                Evil twin
              </Button>
            </span>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            onClick={handleRefresh}
            sx={{ minWidth: 0, px: 1.25, whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {loadError ? <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert> : null}
      {showEvilTwin && !evilTwinLoading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Red markers are unverified SSID impersonation matches (not confirmed attacks).
          Location is approximate — near the hearing device, not the foreign AP.
          Only live public WiFi SSIDs with two or more hearers in the last {EVIL_TWIN_SURVEY_MAX_AGE_HOURS} h are shown.
        </Alert>
      ) : null}
      {showEvilTwin && evilTwinError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{evilTwinError}</Alert>
      ) : null}
      {showEvilTwin && unplottableEvilTwins.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {unplottableEvilTwins.length} match{unplottableEvilTwins.length === 1 ? '' : 'es'} without hearer GPS
          </Typography>
          <Stack spacing={0.5}>
            {unplottableEvilTwins.slice(0, 5).map((alert) => (
              <Typography key={`${alert.ssid}|${alert.foreign_bssid}`} variant="body2">
                {alert.ssid} on {alert.foreign_bssid}
                {' '}({alert.hearer_count} hearers, {Math.round(alert.strongest_rssi_dbm)} dBm)
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Box sx={{ position: 'relative' }}>
        {loading && surveyDevices.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ChannelSurveyMapView
            markers={mapMarkers}
            markerRadiusM={SURVEY_MAP_MARKER_RADIUS_M}
            selectedMarkerId={selectedMarkerId}
            onSelectMarker={setSelectedMarkerId}
            emptyMessage={mapEmptyMessage}
          />
        )}
      </Box>
    </Box>
  );
};

export default ChannelSurveyTab;
